import { ERROR_CODES } from '@instanexus/contracts';
import { z } from 'zod';
import { ApiError } from './api-error.js';
/**
 * The web surface is unversioned: the SPA ships from the same commit as the API
 * and `@instanexus/contracts` is already their compile-time contract
 * (docs/002-architecture.md). Mobile/device/public clients are separate work.
 */
export const WEB_API_PREFIX = '/api/web';
export const DEVICE_API_PREFIX = '/api/device/v1';
/** S41 — the Visit Pass public surface (no session; the pass token rides in `X-Visit-Pass`). */
export const PUBLIC_API_PREFIX = '/api/public/v1';
export const HEALTH_PATH = '/api/health';
/** Mirrors `ListMeta` in `@instanexus/contracts`. */
export const listMetaSchema = z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
});
/** `visitor.flow.manage`, `visitor.visit.self-check-out` — the shape of every permission name. */
const PERMISSION_NAME = /^[a-z][a-z0-9_-]*(\.[a-z][a-z0-9_-]*)+$/;
/**
 * The error middleware is the only thing that writes this shape, and `error` is
 * always a declared code — a prose `error` fails this parse on purpose.
 *
 * `requestId` and `debug` are both `.optional()` here even though the server always
 * sends a `requestId` (`ErrorEnvelope.requestId` in `@instanexus/contracts` is
 * required) — this is a defensive CLIENT-SIDE parse of wire data, same posture as
 * `details` below, and a fallback to the response header (`toApiError`) already
 * covers the rare case a body fails this shape. `debug` is genuinely optional: it
 * is present only when the server's own `NODE_ENV` is `'development'`.
 */
export const errorEnvelopeSchema = z.object({
    error: z.enum(ERROR_CODES),
    statusCode: z.number().int(),
    requestId: z.string().optional(),
    details: z.unknown().optional(),
    debug: z
        .object({
        name: z.string(),
        message: z.string(),
        stack: z.string().optional(),
    })
        .optional(),
    // Forward-compatible and diagnostic only (`ApiError.permission`). Constrained to the
    // permission-name shape so nothing but a dotted code can ever reach the console seam.
    permission: z.string().regex(PERMISSION_NAME).max(100).optional().catch(undefined),
});
/** `{ data: T }` — single resource, read or write. */
export function dataEnvelopeSchema(payload) {
    return z.object({ data: payload });
}
/** `{ data: T[], meta }` — every list endpoint. */
export function listEnvelopeSchema(payload) {
    return z.object({ data: z.array(payload), meta: listMetaSchema });
}
function resolveFetch(candidate) {
    if (candidate !== undefined)
        return candidate;
    const globalFetch = globalThis.fetch;
    if (typeof globalFetch !== 'function') {
        throw new TypeError('No fetch implementation available; pass one to createClient().');
    }
    return globalFetch.bind(globalThis);
}
/**
 * The `RequestInit` base every one of the four request paths below starts from — extracted so
 * a future fifth path cannot forget `cache: 'no-store'`.
 *
 * `cache: 'no-store'` is set on EVERY request this SDK sends, unconditionally: a CDN bug
 * (fixed separately in `infra/`, defended server-side in `apps/api/src/lib/cache-control.ts`)
 * once replaced an API 403/404 with a 200 HTML page, and a browser that heuristically cached
 * that HTML against the GET URL kept replaying it for hours after the underlying condition
 * cleared — every subsequent call this SDK made looked like the same failure even though the
 * server had long since recovered. `no-store` is the only `RequestCache` value that forbids
 * the browser (and any intermediate shared cache) from ever reusing or revalidating a stored
 * response, so a retry always reaches the real server. This is defense in depth on top of the
 * API's own `Cache-Control: no-store` response header — neither alone is sufficient: the
 * response header does nothing once a browser has already cached a stale response under the
 * CDN-bug condition, and `cache: 'no-store'` on the request is what stops that cache from ever
 * being consulted in the first place.
 */
function buildInit(options) {
    const init = {
        method: options.method,
        headers: options.headers,
        cache: 'no-store',
    };
    if (options.credentials !== undefined)
        init.credentials = options.credentials;
    if (options.body !== undefined)
        init.body = options.body;
    if (options.signal !== undefined)
        init.signal = options.signal;
    return init;
}
function buildUrl(baseUrl, path, query) {
    const url = new URL(`${baseUrl.replace(/\/+$/, '')}${path}`);
    for (const [key, value] of Object.entries(query ?? {})) {
        if (value === undefined || value === null || value === '')
            continue;
        url.searchParams.set(key, String(value));
    }
    return url.toString();
}
/**
 * Empty body parses to `undefined` silently (a 204 has no body to complain
 * about) — only a NON-empty body that fails `JSON.parse` counts as the
 * `'parse'` failure kind, reported via `onParseFailure`. The return contract is
 * unchanged either way: an unparseable body still resolves to `undefined`,
 * exactly as before this hook existed.
 */
function parseJson(text, onParseFailure) {
    if (text.length === 0)
        return undefined;
    try {
        return JSON.parse(text);
    }
    catch (err) {
        onParseFailure(err);
        return undefined;
    }
}
/**
 * The `filename` out of a `content-disposition` header.
 *
 * Accepts `attachment; filename="visits-2026-08-13.csv"` and the unquoted form.
 * Returns `undefined` for a missing header, a header with no `filename`, or a value
 * that is empty after sanitisation.
 *
 * SANITISED, not trusted: any `/`, `\` or leading `.` is stripped, because this value
 * ends up in an `<a download>` attribute. A server that is compromised or simply wrong
 * must not be able to suggest `../../.bashrc` to the browser.
 *
 * RFC 5987's `filename*=UTF-8''…` form is deliberately NOT parsed — our own route
 * emits a plain ASCII date-stamped name, and the caller always has its own fallback.
 */
export function parseContentDispositionFilename(header) {
    if (header === null)
        return undefined;
    const match = /filename=(?:"([^"]*)"|([^;]+))/i.exec(header);
    if (match === null)
        return undefined;
    const raw = (match[1] ?? match[2] ?? '').trim();
    if (raw.length === 0)
        return undefined;
    // Take the final path segment so an attacker-controlled `../` cannot escape
    // the download directory, then strip any remaining leading dot(s).
    const segments = raw.split(/[/\\]/);
    const last = segments[segments.length - 1] ?? '';
    const sanitized = last.replace(/^\.+/, '');
    return sanitized.length === 0 ? undefined : sanitized;
}
/**
 * Exactly one credential source per `auth` (default `'user'`) — see the doc comment
 * on `RequestOptions.auth` for why there are two credential kinds. A provider that
 * is absent, or resolves to nothing, simply omits the header rather than sending a
 * hollow Bearer value. Shared by `request` and `requestText` so there is exactly one
 * place this resolution happens.
 */
async function resolveAuthHeader(auth, getAuthToken, getDeviceToken) {
    const tokenProvider = auth === 'device' ? getDeviceToken : auth === 'none' ? undefined : getAuthToken;
    const token = tokenProvider === undefined ? undefined : await tokenProvider();
    return typeof token === 'string' && token.length > 0 ? `Bearer ${token}` : undefined;
}
/**
 * The envelope-missing fallback's status -> code table. Covers exactly the statuses the API
 * itself ever sends with a specific meaning a caller should be able to tell apart from "the
 * server is broken" — `errors.ts`'s own members, never invented ones. Anything else (including
 * every 5xx) stays `INTERNAL_ERROR`: a status this client has no more specific code for really
 * is, from the caller's point of view, indistinguishable from a server fault.
 *
 * This is the fix for the CDN-passthrough bug (docs: `apps/api/src/lib/cache-control.ts`): a
 * mangled 403/404 — real HTTP status, but a body that is a CDN's HTML error page instead of
 * the JSON envelope — used to fall all the way to `INTERNAL_ERROR` regardless of status, which
 * the UI renders as "Something went wrong on our end." A permission denial or a missing
 * resource is not that, and should not read as our bug.
 */
const STATUS_FALLBACK_CODE = {
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    429: 'RATE_LIMITED',
};
/**
 * A failed response, or a response we cannot trust, always becomes an `ApiError`
 * with a real `ErrorCode`. When the body does not match the error envelope at all (a CDN's
 * HTML error page, a proxy's plain-text body, truncated JSON), the real HTTP status is still
 * trustworthy — it came from the transport, not from the unparseable body — so it is
 * classified via `STATUS_FALLBACK_CODE` rather than collapsed to `INTERNAL_ERROR`
 * unconditionally. Only a status with no more specific meaning (every 5xx, and any status
 * this table does not name) still becomes `INTERNAL_ERROR`, keeping the real HTTP status for
 * logs either way.
 */
function toApiError(status, payload, headerRequestId, request) {
    const parsed = errorEnvelopeSchema.safeParse(payload);
    if (!parsed.success) {
        const code = STATUS_FALLBACK_CODE[status] ?? 'INTERNAL_ERROR';
        return new ApiError(code, status, undefined, requestIdOf(headerRequestId), undefined, {
            request,
        });
    }
    // The body's own `requestId` wins when present (it is the value the server actually
    // stamped on this response); the header is the fallback for a body that omitted it.
    return new ApiError(parsed.data.error, parsed.data.statusCode, parsed.data.details, requestIdOf(parsed.data.requestId ?? headerRequestId), parsed.data.debug, { permission: parsed.data.permission, request });
}
/**
 * `ApiError.requestId` is REQUIRED (issue #57), so the one case that could leave it
 * empty — a response that carried neither the header nor a `requestId` in its body —
 * gets a synthetic id minted here rather than a hole in the type.
 *
 * The `client-` prefix is the whole point: an operator who is handed one of these can
 * tell at a glance that it will NOT be found in the server's logs, because the server
 * never sent an id for this response. That is a far more useful thing to report than
 * the blank the UI used to show, and it still correlates the user's screenshot to the
 * browser session's other reports. Not a UUID: `crypto.randomUUID` is unavailable on
 * insecure origins, and this needs no collision resistance beyond one browser tab.
 */
function requestIdOf(requestId) {
    if (requestId !== undefined && requestId !== '')
        return requestId;
    return `client-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
/**
 * The one place `onError` is ever invoked. A throwing observer must never break
 * the request it was only supposed to be reporting on, so the call is wrapped
 * and any throw is discarded — see the doc comment on `SdkErrorObserver`.
 */
function emitError(onError, event) {
    if (onError === undefined)
        return;
    try {
        onError(event);
    }
    catch {
        // Diagnostic hook only — a bug in the caller's observer is not this
        // request's problem. Nothing to recover here; see `SdkErrorObserver`.
    }
}
/** The header the API always echoes back (`REQUEST_ID_HEADER`, apps/api/src/lib/request-context.ts). */
const REQUEST_ID_HEADER = 'x-request-id';
export function createTransport(options) {
    const fetchImpl = resolveFetch(options.fetch);
    const { baseUrl, getAuthToken, getDeviceToken, onError } = options;
    return {
        async request(request) {
            const method = request.method ?? 'GET';
            const headers = { ...request.headers, accept: 'application/json' };
            const authHeader = await resolveAuthHeader(request.auth, getAuthToken, getDeviceToken);
            if (authHeader !== undefined)
                headers.authorization = authHeader;
            const hasBody = request.body !== undefined;
            if (hasBody)
                headers['content-type'] = 'application/json';
            const init = buildInit({
                method,
                headers,
                signal: request.signal,
                body: hasBody ? JSON.stringify(request.body) : undefined,
                // The durable session cookie (adr/0021) is `HttpOnly`, so the browser attaches it —
                // but only if we ask. Without this, `/auth/session` and `/auth/logout` send no cookie
                // and a reload signs the user out exactly as it did before the cookie existed.
                //
                // `same-origin`, NOT `include`: every SPA reaches its own `/api/*` as a same-origin
                // CloudFront behaviour, so this is all that is needed, and it keeps the credential off
                // any genuinely cross-origin request the SDK is ever pointed at.
                credentials: 'same-origin',
            });
            let response;
            try {
                response = await fetchImpl(buildUrl(baseUrl, request.path, request.query), init);
            }
            catch (err) {
                // No response was ever received, so there is no status and no
                // `x-request-id` to read — this rethrows the exact error `fetch`
                // threw, unchanged, same as before this observer existed.
                emitError(onError, { kind: 'network', method, path: request.path, err });
                throw err;
            }
            // Echoed on every response, success or failure (`REQUEST_ID_HEADER`) —
            // read once here so every event/error below can carry it.
            const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
            const payload = parseJson(await response.text(), (err) => {
                emitError(onError, {
                    kind: 'parse',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err,
                });
            });
            if (!response.ok) {
                const apiError = toApiError(response.status, payload, requestId, { method, path: request.path });
                emitError(onError, {
                    kind: 'http',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err: apiError,
                });
                throw apiError;
            }
            const parsed = request.schema.safeParse(payload);
            if (!parsed.success) {
                emitError(onError, {
                    kind: 'validation',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err: parsed.error,
                });
                throw new ApiError('INTERNAL_ERROR', response.status, undefined, requestIdOf(requestId));
            }
            return parsed.data;
        },
        async requestText(request) {
            const method = request.method ?? 'GET';
            const headers = { accept: request.accept };
            const authHeader = await resolveAuthHeader(request.auth, getAuthToken, getDeviceToken);
            if (authHeader !== undefined)
                headers.authorization = authHeader;
            // Serialized exactly as `request()` serializes its own body — `accept` still names the
            // NON-JSON response this method exists for, so the two headers disagree on purpose.
            const hasBody = request.body !== undefined;
            if (hasBody)
                headers['content-type'] = 'application/json';
            const init = buildInit({
                method,
                headers,
                signal: request.signal,
                body: hasBody ? JSON.stringify(request.body) : undefined,
            });
            let response;
            try {
                response = await fetchImpl(buildUrl(baseUrl, request.path, request.query), init);
            }
            catch (err) {
                // No response was ever received — rethrow the exact error `fetch`
                // threw, unchanged, same as `request`.
                emitError(onError, { kind: 'network', method, path: request.path, err });
                throw err;
            }
            const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
            const raw = await response.text();
            if (!response.ok) {
                const payload = parseJson(raw, (err) => {
                    emitError(onError, {
                        kind: 'parse',
                        status: response.status,
                        method,
                        path: request.path,
                        requestId,
                        err,
                    });
                });
                const apiError = toApiError(response.status, payload, requestId, { method, path: request.path });
                emitError(onError, {
                    kind: 'http',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err: apiError,
                });
                throw apiError;
            }
            // NO schema, NO safeParse, NO `kind: 'validation'` event — there is
            // nothing to validate. A successful response is returned even if
            // `contentType` is unexpected; the caller decides what to do with it.
            return {
                body: raw,
                contentType: response.headers.get('content-type') ?? undefined,
                filename: parseContentDispositionFilename(response.headers.get('content-disposition')),
            };
        },
        async requestBlob(request) {
            const method = 'GET';
            const headers = { accept: request.accept };
            const authHeader = await resolveAuthHeader(request.auth, getAuthToken, getDeviceToken);
            if (authHeader !== undefined)
                headers.authorization = authHeader;
            const init = buildInit({ method, headers, signal: request.signal });
            let response;
            try {
                response = await fetchImpl(buildUrl(baseUrl, request.path, request.query), init);
            }
            catch (err) {
                emitError(onError, { kind: 'network', method, path: request.path, err });
                throw err;
            }
            const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
            // THE FAILURE BRANCH IS READ AS TEXT AND THE SUCCESS BRANCH AS A BLOB, and the two are
            // mutually exclusive on purpose: a `Response` body can be consumed exactly once, so
            // reading text first "just in case" would leave nothing to decode on the happy path.
            // A failure from this route still carries the normal `{ error, statusCode }` envelope
            // (the API's error middleware does not care that the success body is binary).
            if (!response.ok) {
                const payload = parseJson(await response.text(), (err) => {
                    emitError(onError, {
                        kind: 'parse',
                        status: response.status,
                        method,
                        path: request.path,
                        requestId,
                        err,
                    });
                });
                const apiError = toApiError(response.status, payload, requestId, { method, path: request.path });
                emitError(onError, {
                    kind: 'http',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err: apiError,
                });
                throw apiError;
            }
            // NO schema, NO safeParse, NO `kind: 'validation'` event — there is nothing to
            // validate, same as `requestText`.
            return {
                blob: await response.blob(),
                contentType: response.headers.get('content-type') ?? undefined,
            };
        },
        async requestUpload(request) {
            const method = 'POST';
            const headers = {
                ...request.headers,
                accept: 'application/json',
                'content-type': request.contentType,
            };
            const authHeader = await resolveAuthHeader(request.auth, getAuthToken, getDeviceToken);
            if (authHeader !== undefined)
                headers.authorization = authHeader;
            const init = buildInit({ method, headers, signal: request.signal, body: request.body });
            let response;
            try {
                response = await fetchImpl(buildUrl(baseUrl, request.path, request.query), init);
            }
            catch (err) {
                // No response was ever received, so there is no status and no
                // `x-request-id` to read — this rethrows the exact error `fetch`
                // threw, unchanged, same as `request`.
                emitError(onError, { kind: 'network', method, path: request.path, err });
                throw err;
            }
            // Echoed on every response, success or failure (`REQUEST_ID_HEADER`) —
            // read once here so every event/error below can carry it.
            const requestId = response.headers.get(REQUEST_ID_HEADER) ?? undefined;
            const payload = parseJson(await response.text(), (err) => {
                emitError(onError, {
                    kind: 'parse',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err,
                });
            });
            if (!response.ok) {
                const apiError = toApiError(response.status, payload, requestId, { method, path: request.path });
                emitError(onError, {
                    kind: 'http',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err: apiError,
                });
                throw apiError;
            }
            const parsed = request.schema.safeParse(payload);
            if (!parsed.success) {
                emitError(onError, {
                    kind: 'validation',
                    status: response.status,
                    method,
                    path: request.path,
                    requestId,
                    err: parsed.error,
                });
                throw new ApiError('INTERNAL_ERROR', response.status, undefined, requestIdOf(requestId));
            }
            return parsed.data;
        },
    };
}
//# sourceMappingURL=transport.js.map